/**
 * SetLaunchEntityCommand - Generic command for setting launch entity (StepNode, StrategyBlock)
 *
 * Implements the Command pattern for undoable launch entity setting.
 * Delegates to FlowCoordinator for actual logic.
 *
 * Generic Architecture:
 * - TEntity extends FlowEntity (StepNode, StrategyBlock)
 * - TService (StepNodeScreenService, StrategyBlockService)
 * - Uses ONLY generic coordinator methods (getLaunchEntityId, setLaunchEntity)
 * - Single unified command for both blocks and screens
 *
 * Usage:
 * ```typescript
 * // Set launch V2 StepNode
 * const command = new SetLaunchEntityCommand<FlowStepNode, StepNodeScreenService>(
 *   'screen-1',
 *   screenContext
 * );
 *
 * // Set launch block
 * const command = new SetLaunchEntityCommand<StrategyBlock, StrategyBlockService>(
 *   'block-1',
 *   blockContext
 * );
 *
 * await commandManager.execute(command);
 * ```
 *
 * Related Requirements:
 * - FR-P1-003: Commands use coordinator ONLY for all operations
 * - FR-P3-001: Generic FlowCoordinator interface
 * - FR-P3-004: Generic CommandContext
 */

import { Command, CommandResult } from './Command';
import { CommandContext } from './CommandContext';
import type { FlowEntity } from '@modules/reactFlow/types/flow-canvas-types';

/**
 * SetLaunchEntityCommand - Generic command for setting launch entity
 *
 * @template TEntity - Entity type extending FlowEntity (StepNode, StrategyBlock)
 * @template TService - Firebase service type (StepNodeScreenService, StrategyBlockService)
 */
export class SetLaunchEntityCommand<
  TEntity extends FlowEntity,
  TService
> implements Command {
  public readonly id: string;
  public readonly description: string;
  public readonly timestamp: number;

  // Backup for undo
  private previousLaunchEntityId: string | null = null;
  private deletedTransitionIds: string[] = [];
  private affectedEntitiesBackup?: Map<string, TEntity>;

  /**
   * @param entityId - Entity ID to set as launch
   * @param context - Command execution context
   */
  constructor(
    private entityId: string,
    private context: CommandContext<TEntity, TService>
  ) {
    this.id = `set-launch-entity-${entityId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.description = `Set launch entity ${entityId}`;
    this.timestamp = Date.now();
  }

  /**
   * Execute command - Set launch entity via coordinator
   *
   * Flow:
   * 1. Get current launch entity ID for undo
   * 2. Call coordinator.setLaunchEntity() - handles:
   *    - Deleting incoming edges to launch entity
   *    - Backing up affected entities
   *    - Unsetting previous launch entity
   *    - Setting new launch entity
   *    - Canvas visual indicator updates
   *    - Firebase persistence
   * 3. Store deletion data for undo
   * 4. Return success result
   *
   * @returns Promise resolving with command result
   */
  async execute(): Promise<CommandResult> {
    try {
      console.log(`[SetLaunchEntityCommand] Executing set launch for entity: ${this.entityId}`);

      // Step 1: Backup current launch entity ID for undo
      this.previousLaunchEntityId = await this.context.coordinator.getLaunchEntityId();

      // Step 2: Set new launch entity via coordinator (NOW returns deletion info)
      const result = await this.context.coordinator.setLaunchEntity(this.entityId);

      // Step 3: Store deletion data for undo
      this.deletedTransitionIds = result.deletedTransitionIds;
      this.affectedEntitiesBackup = result.affectedEntitiesBackup;

      console.log(`[SetLaunchEntityCommand] ✅ Launch entity set: ${this.entityId}. Deleted ${this.deletedTransitionIds.length} incoming edge(s).`);

      return {
        success: true,
        affectedEntities: {
          screens: this.previousLaunchEntityId
            ? [this.previousLaunchEntityId, this.entityId]
            : [this.entityId],
          transitions: this.deletedTransitionIds
        }
      };
    } catch (error) {
      console.error('[SetLaunchEntityCommand] ❌ Set launch failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error'),
        affectedEntities: {}
      };
    }
  }

  /**
   * Undo command - Restore previous launch entity via coordinator
   *
   * Flow:
   * 1. If no previous launch entity exists, operation cannot be undone
   * 2. Restore previous launch entity (this will delete ITS incoming edges)
   * 3. Restore affected entities from backup (restores their edges)
   * 4. Return success result
   *
   * Note: Edges are automatically restored because they are stored in entity.struct
   * Restoring the entity struct automatically restores all edges!
   *
   * @returns Promise resolving with command result
   */
  async undo(): Promise<CommandResult> {
    if (!this.previousLaunchEntityId) {
      return {
        success: false,
        error: new Error('No previous launch entity to restore'),
        affectedEntities: {}
      };
    }

    try {
      console.log(`[SetLaunchEntityCommand] Undoing set launch - restoring: ${this.previousLaunchEntityId}`);

      // Step 1: Restore previous launch entity (this will delete ITS incoming edges)
      const restoreResult = await this.context.coordinator.setLaunchEntity(this.previousLaunchEntityId);

      // Step 2: Restore affected entities backup (restores their edges)
      if (this.affectedEntitiesBackup && this.affectedEntitiesBackup.size > 0) {
        const entitiesToRestore = Array.from(this.affectedEntitiesBackup.values());
        await this.context.coordinator.restoreEntities(entitiesToRestore, this.affectedEntitiesBackup);
        console.log(`[SetLaunchEntityCommand] Restored ${entitiesToRestore.length} affected entity/entities with their edges`);
      }

      console.log('[SetLaunchEntityCommand] ✅ Undo successful');

      return {
        success: true,
        affectedEntities: {
          screens: [this.previousLaunchEntityId, this.entityId],
          transitions: this.deletedTransitionIds
        }
      };
    } catch (error) {
      console.error('[SetLaunchEntityCommand] ❌ Undo failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Undo failed'),
        affectedEntities: {}
      };
    }
  }

  /**
   * Check if command can be executed
   * @returns true if entity exists
   */
  canExecute(): boolean {
    const entity = this.context.coordinator.getEntity(this.entityId);
    return entity !== undefined;
  }

  /**
   * Check if command can be undone
   * @returns true if previous launch entity was backed up
   */
  canUndo(): boolean {
    return this.previousLaunchEntityId !== null;
  }
}
